/**
 * Gaming-themed Share & Earn panel binder.
 */
import { getAccountSnapshot, subscribeAccount } from "./firebase/authAccount";
import {
  fetchReferralWallet,
  shareReferralInvite,
  saveReferralKyc,
  requestReferralWithdraw,
  claimStoredReferralIfNeeded,
  buildReferralShareUrl,
  buildReferralShareText,
  type ReferralWalletView,
} from "./referral";
import { buildMsStoreReferralUrl } from "./shareCopy";

function fmtInr(n: number): string {
  return `₹${Math.round(n)}`;
}

function unlockLabel(ms: number): string {
  const days = Math.max(0, Math.ceil((ms - Date.now()) / 86_400_000));
  if (days <= 0) return "Unlocking…";
  return days === 1 ? "Unlocks in 1 day" : `Unlocks in ${days} days`;
}

export function bindShareEarnUi(opts: {
  root: HTMLElement;
  onToast: (msg: string) => void;
}): void {
  const { root, onToast } = opts;
  let refreshSeq = 0;

  async function paint(data: ReferralWalletView): Promise<void> {
    const codeEl = root.querySelector("#earn-code")!;
    const playLinkEl = root.querySelector<HTMLInputElement>("#earn-link-play")!;
    const msLinkEl = root.querySelector<HTMLInputElement>("#earn-link-ms")!;
    const pendingEl = root.querySelector("#earn-pending")!;
    const availableEl = root.querySelector("#earn-available")!;
    const lifeEl = root.querySelector("#earn-lifetime")!;
    const unlocksEl = root.querySelector("#earn-unlocks")!;
    const wdEl = root.querySelector("#earn-withdrawals")!;
    const nameIn = root.querySelector<HTMLInputElement>("#earn-kyc-name")!;
    const upiIn = root.querySelector<HTMLInputElement>("#earn-kyc-upi")!;

    codeEl.textContent = data.referralCode;
    playLinkEl.value = buildReferralShareUrl(data.referralCode);
    msLinkEl.value = buildMsStoreReferralUrl(data.referralCode);
    pendingEl.textContent = fmtInr(data.wallet.pendingInr);
    availableEl.textContent = fmtInr(data.wallet.availableInr);
    lifeEl.textContent = fmtInr(data.wallet.lifetimeEarnedInr);

    if (data.wallet.kycName) nameIn.value = data.wallet.kycName;
    if (data.wallet.kycUpi) upiIn.value = data.wallet.kycUpi;

    unlocksEl.innerHTML = data.pendingUnlocks.length
      ? data.pendingUnlocks
          .map(
            (u: { amountInr: number; unlockAtMs: number }) =>
              `<li><strong>${fmtInr(u.amountInr)}</strong> · ${unlockLabel(u.unlockAtMs)}</li>`,
          )
          .join("")
      : `<li class="muted">No pending unlocks yet — share your link!</li>`;

    wdEl.innerHTML = data.withdrawals.length
      ? data.withdrawals
          .map(
            (w: { amountInr: number; status: string }) =>
              `<li><strong>${fmtInr(w.amountInr)}</strong> · ${w.status.toUpperCase()}</li>`,
          )
          .join("")
      : `<li class="muted">No withdraw requests yet.</li>`;

    root.dataset.ready = "1";
    root.dataset.code = data.referralCode;
    root.dataset.available = String(data.wallet.availableInr);
    root.dataset.min = String(data.rules.minWithdrawInr);
  }

  async function refresh(): Promise<void> {
    const seq = ++refreshSeq;
    const snap = getAccountSnapshot();
    const gate = root.querySelector<HTMLElement>("#earn-gate")!;
    const gateRow = root.querySelector<HTMLElement>("#earn-gate-row");
    const body = root.querySelector<HTMLElement>("#earn-body")!;
    const status = root.querySelector<HTMLElement>("#earn-status")!;

    if (!snap.configured) {
      gate.hidden = false;
      gateRow && (gateRow.hidden = false);
      body.hidden = true;
      status.hidden = true;
      gate.textContent = "Firebase not configured — Share & Earn needs sign-in.";
      return;
    }

    if (!snap.user || snap.isAnonymous) {
      gate.hidden = false;
      gateRow && (gateRow.hidden = false);
      body.hidden = true;
      status.hidden = true;
      gate.textContent = "Sign in with Google to open your referral wallet (code, ₹ balance, withdraw).";
      return;
    }

    // Keep wallet shell visible while loading — never blank the whole card.
    gate.hidden = true;
    gateRow && (gateRow.hidden = true);
    body.hidden = false;
    status.hidden = false;
    status.textContent = "Loading wallet…";
    status.classList.remove("is-error");

    try {
      await claimStoredReferralIfNeeded();
      if (seq !== refreshSeq) return;
      const data = await fetchReferralWallet();
      if (seq !== refreshSeq) return;
      await paint(data);
      status.hidden = true;
      status.textContent = "";
    } catch (e) {
      if (seq !== refreshSeq) return;
      const msg = e instanceof Error ? e.message : "Could not load Share & Earn.";
      status.hidden = false;
      status.classList.add("is-error");
      status.textContent = `${msg} — tap Retry.`;
      // Keep body visible with last values / zeros so UI never disappears.
      body.hidden = false;
      gate.hidden = true;
      gateRow && (gateRow.hidden = true);
    }
  }

  root.querySelector("#btn-earn-retry")?.addEventListener("click", () => {
    void refresh();
  });

  root.querySelector("#btn-earn-share")?.addEventListener("click", () => {
    const code = root.dataset.code;
    if (!code) {
      onToast("Wallet still loading — wait a second, then try again.");
      void refresh();
      return;
    }
    const name = getAccountSnapshot().displayName || "Piano Cadet";
    void shareReferralInvite(code, name)
      .then((how) => {
        onToast(
          how === "shared"
            ? "Referral link shared — earn ₹250 when they buy Full Course!"
            : "Referral link ready — paste it anywhere.",
        );
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        onToast(e instanceof Error ? e.message : "Share failed");
      });
  });

  root.querySelector("#btn-earn-copy")?.addEventListener("click", async () => {
    const code = root.dataset.code;
    if (!code) {
      onToast("Link not ready yet — retry wallet load.");
      void refresh();
      return;
    }
    const name = getAccountSnapshot().displayName || "Piano Cadet";
    const text = buildReferralShareText(code, name);
    try {
      await navigator.clipboard.writeText(text);
      onToast("Play + Microsoft Store links copied (with your code)!");
    } catch {
      onToast("Could not copy — long-press the link field.");
    }
  });

  root.querySelectorAll<HTMLButtonElement>("[data-earn-copy]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const input = root.querySelector<HTMLInputElement>(`#${btn.dataset.earnCopy}`);
      const value = input?.value.trim();
      if (!value) {
        onToast("Link not ready yet — retry wallet load.");
        void refresh();
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        input!.select();
        document.execCommand("copy");
      }
      const label = btn.textContent;
      btn.textContent = "Copied ✓";
      window.setTimeout(() => (btn.textContent = label), 1500);
      onToast(
        btn.dataset.earnCopy === "earn-link-ms"
          ? "Microsoft Store link copied (with your code)!"
          : "Google Play link copied (with your code)!",
      );
    });
  });

  root.querySelector("#btn-earn-kyc")?.addEventListener("click", () => {
    const name = root.querySelector<HTMLInputElement>("#earn-kyc-name")!.value;
    const upi = root.querySelector<HTMLInputElement>("#earn-kyc-upi")!.value;
    void saveReferralKyc(name, upi)
      .then(() => {
        onToast("Name + UPI saved for payouts.");
        return refresh();
      })
      .catch((e) => onToast(e instanceof Error ? e.message : "KYC save failed"));
  });

  root.querySelector("#btn-earn-withdraw")?.addEventListener("click", () => {
    const available = Number(root.dataset.available || 0);
    const min = Number(root.dataset.min || 1000);
    if (available < min) {
      onToast(`Need at least ${fmtInr(min)} available to withdraw.`);
      return;
    }
    void requestReferralWithdraw(available)
      .then((r) => {
        onToast(r.message);
        return refresh();
      })
      .catch((e) => onToast(e instanceof Error ? e.message : "Withdraw failed"));
  });

  subscribeAccount(() => {
    void refresh();
  });
  // Retry after auth/user-doc settle
  window.setTimeout(() => void refresh(), 1200);
  window.setTimeout(() => void refresh(), 3500);
  void refresh();
}

/** Scroll Profile Share & Earn card into view (hub banner). */
export function focusShareEarnPanel(): void {
  const el = document.querySelector<HTMLElement>("#share-earn-root");
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.classList.add("is-pulse");
  window.setTimeout(() => el.classList.remove("is-pulse"), 1600);
}
